/**
 * Tools Module - Main Export
 * 
 * This module exports all tool-related functionality including
 * types, registry, and tool implementations.
 */

// Export types
export * from './tool-types';

// Export registry
export { toolRegistry, ToolRegistry } from './tool-registry';

// Export active tool implementations (Insurance showcase)
export {
  incidentReportToolSchema,
  incidentReportToolImplementation
} from './implementations/incident-report-tool';

// Export Vodafone tools (available but not registered by default)
export {
  searchToolVodafoneSchema,
  searchToolVodafoneImplementation
} from './implementations/search-tool-vodafone';

export {
  groundingToolVodafoneSchema,
  groundingToolVodafoneImplementation
} from './implementations/grounding-tool-vodafone';

// Cluster Reply showcase tools (use initializeClusterReplyTools())
export {
  searchToolClusterReplySchema,
  searchToolClusterReplyImplementation
} from './implementations/search-tool-cluster-reply';

export {
  groundingToolClusterReplySchema,
  groundingToolClusterReplyImplementation
} from './implementations/grounding-tool-cluster-reply';

// Metro eLearning coaching tools
export {
  supervisorToolMetroSchema,
  supervisorToolMetroImplementation
} from './implementations/supervisor-tool-metro';

export {
  stateChangeToolMetroSchema,
  stateChangeToolMetroImplementation
} from './implementations/state-change-tool-metro';

// Metro course search tool
export {
  searchCoursesToolMetroSchema,
  searchCoursesToolMetroImplementation
} from './implementations/search-courses-tool-metro';
export {
  addGoalCardToolMetroSchema,
  addGoalCardToolMetroImplementation
} from './implementations/add-goal-card-tool-metro';
export {
  showKeywordsToolMetroSchema,
  showKeywordsToolMetroImplementation
} from './implementations/show-keywords-tool-metro';
export {
  selectCourseToolMetroSchema,
  selectCourseToolMetroImplementation
} from './implementations/select-course-tool-metro';
export {
  flipCourseCardToolMetroSchema,
  flipCourseCardToolMetroImplementation
} from './implementations/flip-course-card-tool-metro';

// Provinzial eVB customer service tools
export {
  issueEvbSchema,
  issueEvbImplementation,
  lookupEvbSchema,
  lookupEvbImplementation,
  calculatePremiumSchema,
  calculatePremiumImplementation
} from './implementations/evb-tools-provinzial';

export {
  stateChangeToolProvinzialSchema,
  stateChangeToolProvinzialImplementation
} from './implementations/state-change-tool-provinzial';

// CUPRA advisor tools
export {
  searchToolCupraSchema,
  searchToolCupraImplementation
} from './implementations/search-tool-cupra';

export {
  advisorToolCupraSchema,
  advisorToolCupraImplementation
} from './implementations/advisor-tool-cupra';

export {
  stateChangeToolCupraSchema,
  stateChangeToolCupraImplementation
} from './implementations/state-change-tool-cupra';

export {
  qrCodeToolCupraSchema,
  qrCodeToolCupraImplementation
} from './implementations/qr-code-tool-cupra';

/**
 * Initialize all tools in the registry
 * Call this function once during application startup
 * 
 * Currently only registers tools for the Insurance showcase.
 * Vodafone tools are available in the codebase but not registered.
 */
import { toolRegistry } from './tool-registry';
import {
  incidentReportToolSchema,
  incidentReportToolImplementation
} from './implementations/incident-report-tool';

export function initializeTools(): void {
  console.log('🔧 Initializing tools for Insurance showcase...');

  // Register incident report tool (for insurance showcase)
  toolRegistry.register(
    'collect_incident_report',
    incidentReportToolSchema,
    incidentReportToolImplementation
  );

  console.log(`✅ Initialized ${toolRegistry.count} tool(s)`);
  console.log(`📋 Available tools: ${toolRegistry.getToolNames().join(', ')}`);
}

/**
 * Initialize Vodafone tools (for future Vodafone showcase)
 * Call this function instead of initializeTools() to use Vodafone tools
 */
export function initializeVodafoneTools(): void {
  console.log('🔧 Initializing tools for Vodafone showcase...');

  const {
    searchToolVodafoneSchema,
    searchToolVodafoneImplementation
  } = require('./implementations/search-tool-vodafone');

  const {
    groundingToolVodafoneSchema,
    groundingToolVodafoneImplementation
  } = require('./implementations/grounding-tool-vodafone');

  // Register search tool
  toolRegistry.register('search', searchToolVodafoneSchema, searchToolVodafoneImplementation);

  // Register grounding tool
  toolRegistry.register('report_grounding', groundingToolVodafoneSchema, groundingToolVodafoneImplementation);

  console.log(`✅ Initialized ${toolRegistry.count} Vodafone tool(s)`);
  console.log(`📋 Available tools: ${toolRegistry.getToolNames().join(', ')}`);
}

/**
 * Initialize Cluster Reply tools (search + report_grounding) for the Cluster Reply showcase.
 * Clears the registry first so only Cluster Reply tools are active.
 */
export function initializeClusterReplyTools(): void {
  console.log('🔧 Initializing tools for Cluster Reply showcase...');

  toolRegistry.clear();

  const {
    searchToolClusterReplySchema,
    searchToolClusterReplyImplementation
  } = require('./implementations/search-tool-cluster-reply');

  const {
    groundingToolClusterReplySchema,
    groundingToolClusterReplyImplementation
  } = require('./implementations/grounding-tool-cluster-reply');

  toolRegistry.register('search', searchToolClusterReplySchema, searchToolClusterReplyImplementation);
  toolRegistry.register('report_grounding', groundingToolClusterReplySchema, groundingToolClusterReplyImplementation);

  console.log(`✅ Initialized ${toolRegistry.count} Cluster Reply tool(s)`);
  console.log(`📋 Available tools: ${toolRegistry.getToolNames().join(', ')}`);
}

/**
 * Initialize Metro eLearning coaching tools.
 * Clears the registry first so only Metro tools are active.
 */
export function initializeMetroTools(): void {
  console.log('🔧 Initializing tools for Metro eLearning coaching...');

  toolRegistry.clear();

  const {
    setCurrentMetroStateId,
    INITIAL_METRO_STATE
  } = require('../config/metro-conversation-states');

  const {
    supervisorToolMetroSchema,
    supervisorToolMetroImplementation
  } = require('./implementations/supervisor-tool-metro');

  const {
    stateChangeToolMetroSchema,
    stateChangeToolMetroImplementation
  } = require('./implementations/state-change-tool-metro');

  const {
    searchCoursesToolMetroSchema,
    searchCoursesToolMetroImplementation
  } = require('./implementations/search-courses-tool-metro');
  const {
    addGoalCardToolMetroSchema,
    addGoalCardToolMetroImplementation
  } = require('./implementations/add-goal-card-tool-metro');
  const {
    showKeywordsToolMetroSchema,
    showKeywordsToolMetroImplementation
  } = require('./implementations/show-keywords-tool-metro');
  const {
    selectCourseToolMetroSchema,
    selectCourseToolMetroImplementation
  } = require('./implementations/select-course-tool-metro');
  const {
    flipCourseCardToolMetroSchema,
    flipCourseCardToolMetroImplementation
  } = require('./implementations/flip-course-card-tool-metro');
  const {
    revealProgressBarToolMetroSchema,
    revealProgressBarToolMetroImplementation
  } = require('./implementations/reveal-progress-bar-tool-metro');

  setCurrentMetroStateId(INITIAL_METRO_STATE);

  toolRegistry.register('get_coaching_advice', supervisorToolMetroSchema, supervisorToolMetroImplementation);
  toolRegistry.register('set_conversation_state', stateChangeToolMetroSchema, stateChangeToolMetroImplementation);
  toolRegistry.register('search_courses', searchCoursesToolMetroSchema, searchCoursesToolMetroImplementation);
  toolRegistry.register('add_goal_card', addGoalCardToolMetroSchema, addGoalCardToolMetroImplementation);
  toolRegistry.register('show_keywords', showKeywordsToolMetroSchema, showKeywordsToolMetroImplementation);
  toolRegistry.register('select_course', selectCourseToolMetroSchema, selectCourseToolMetroImplementation);
  toolRegistry.register('flip_course_card', flipCourseCardToolMetroSchema, flipCourseCardToolMetroImplementation);
  toolRegistry.register('reveal_progress_bar', revealProgressBarToolMetroSchema, revealProgressBarToolMetroImplementation);

  console.log(`✅ Initialized ${toolRegistry.count} Metro tool(s)`);
  console.log(`📋 Available tools: ${toolRegistry.getToolNames().join(', ')}`);
}

/**
 * Initialize Provinzial eVB customer service tools.
 * Clears the registry first so only Provinzial tools are active.
 */
export function initializeProvinzialTools(): void {
  console.log('🔧 Initializing tools for Provinzial eVB customer service...');

  toolRegistry.clear();

  const {
    setCurrentProvinzialStateId,
    INITIAL_PROVINZIAL_STATE
  } = require('../config/provinzial-conversation-states');

  const {
    issueEvbSchema,
    issueEvbImplementation,
    lookupEvbSchema,
    lookupEvbImplementation,
    calculatePremiumSchema,
    calculatePremiumImplementation
  } = require('./implementations/evb-tools-provinzial');

  const {
    stateChangeToolProvinzialSchema,
    stateChangeToolProvinzialImplementation
  } = require('./implementations/state-change-tool-provinzial');

  setCurrentProvinzialStateId(INITIAL_PROVINZIAL_STATE);

  toolRegistry.register('issue_evb', issueEvbSchema, issueEvbImplementation);
  toolRegistry.register('lookup_evb', lookupEvbSchema, lookupEvbImplementation);
  toolRegistry.register('calculate_premium', calculatePremiumSchema, calculatePremiumImplementation);
  toolRegistry.register('set_conversation_state', stateChangeToolProvinzialSchema, stateChangeToolProvinzialImplementation);

  console.log(`✅ Initialized ${toolRegistry.count} Provinzial tool(s)`);
  console.log(`📋 Available tools: ${toolRegistry.getToolNames().join(', ')}`);
}

/**
 * Initialize CUPRA advisor tools.
 * Clears the registry first so only CUPRA tools are active.
 */
export function initializeCupraTools(): void {
  console.log('🔧 Initializing tools for CUPRA advisor...');

  toolRegistry.clear();

  const { setCurrentCupraStateId, INITIAL_CUPRA_STATE } = require('../config/cupra-conversation-states');

  const { searchToolCupraSchema, searchToolCupraImplementation } = require('./implementations/search-tool-cupra');
  const { advisorToolCupraSchema, advisorToolCupraImplementation } = require('./implementations/advisor-tool-cupra');
  const {
    stateChangeToolCupraSchema,
    stateChangeToolCupraImplementation
  } = require('./implementations/state-change-tool-cupra');
  const { qrCodeToolCupraSchema, qrCodeToolCupraImplementation } = require('./implementations/qr-code-tool-cupra');

  setCurrentCupraStateId(INITIAL_CUPRA_STATE);

  toolRegistry.register('search_cupra_data', searchToolCupraSchema, searchToolCupraImplementation);
  toolRegistry.register('get_cupra_recommendation', advisorToolCupraSchema, advisorToolCupraImplementation);
  toolRegistry.register(
    'set_conversation_state',
    stateChangeToolCupraSchema,
    stateChangeToolCupraImplementation
  );
  toolRegistry.register('show_qr_code', qrCodeToolCupraSchema, qrCodeToolCupraImplementation);

  console.log(`✅ Initialized ${toolRegistry.count} CUPRA tool(s)`);
  console.log(`📋 Available tools: ${toolRegistry.getToolNames().join(', ')}`);
}

/**
 * Convenience function to get all tool schemas for Realtime API
 */
export function getToolSchemas() {
  return toolRegistry.getToolSchemas();
}

/**
 * Convenience function to execute a tool
 */
export async function executeTool(name: string, args: any) {
  return toolRegistry.executeTool(name, args);
}
